import { emptySplitApi } from '../../../../store/emptyApi';

// Types for lookup responses
export interface TeamLookup {
  id: number;
  name: string;
}

export interface AssessmentPeriodLookup {
  id: number;
  name: string;
  start_date_time: string;
}

export interface MetricLookup {
  id: number;
  metric_name: string;
  metric_label: string | null;
  metric_type: number;
}

// Route constants for lookup API
export const LOOKUP_ROUTES = {
  teams: '/api/admin/v1/lookup/teams/',
  assessmentPeriods: '/api/admin/v1/lookup/assessment-periods/',
  metrics: '/api/admin/v1/lookup/metrics/',
} as const;

// Tag types for cache invalidation
export const LookupApiTags = {
  TeamLookup: 'TeamLookup',
  AssessmentPeriodLookup: 'AssessmentPeriodLookup',
  MetricLookup: 'MetricLookup',
} as const;

const taggedApi = emptySplitApi.enhanceEndpoints({
  addTagTypes: Object.values(LookupApiTags),
});

export const lookupApi = taggedApi.injectEndpoints({
  endpoints: (build) => ({
    // Get teams by organization
    getTeamsByOrganization: build.query<TeamLookup[], { organization_id: number }>({
      query: ({ organization_id }) => ({
        url: LOOKUP_ROUTES.teams,
        params: { organization_id },
      }),
      providesTags: (result, error, { organization_id }) => [
        { type: LookupApiTags.TeamLookup, id: organization_id },
      ],
    }),

    // Get assessment periods by organization
    getAssessmentPeriodsByOrganization: build.query<AssessmentPeriodLookup[], { organization_id: number }>({
      query: ({ organization_id }) => ({
        url: LOOKUP_ROUTES.assessmentPeriods,
        params: { organization_id },
      }),
      providesTags: (result, error, { organization_id }) => [
        { type: LookupApiTags.AssessmentPeriodLookup, id: organization_id },
      ],
    }),

    // Get all active metric definitions
    getMetricDefinitionsLookup: build.query<MetricLookup[], void>({
      query: () => LOOKUP_ROUTES.metrics,
      providesTags: [LookupApiTags.MetricLookup],
    }),
  }),
  overrideExisting: false,
});

// Export hooks for use in components
export const {
  useGetTeamsByOrganizationQuery,
  useGetAssessmentPeriodsByOrganizationQuery,
  useGetMetricDefinitionsLookupQuery,
  useLazyGetTeamsByOrganizationQuery,
  useLazyGetAssessmentPeriodsByOrganizationQuery,
} = lookupApi;
